import Axios from "axios"

import type { AuthSnapshot } from "@/api/auth-refresh-coordinator"
import type { AxiosError, InternalAxiosRequestConfig } from "axios"

import { useLogin } from "@/api/auth-api"
import {
  captureAuthSnapshot,
  isAuthSnapshotCurrent,
  recoverFromUnauthorized,
} from "@/api/auth-refresh-coordinator"
import { SettingsService } from "@/services/settings-service"
import {
  TENDER_ADMIN_MODE_HEADER,
  tenderAdminModeHeaderValue,
} from "@/stores/tender-admin-mode-store"

const BASE_API_URL =
  process.env.NODE_ENV === "development"
    ? `http://localhost:${import.meta.env.VITE_BACKEND_PORT || "50505"}/api`
    : "/api"

declare module "axios" {
  export interface AxiosRequestConfig {
    skipAuth?: boolean
    /**
     * Opt a **mutation** into the bounded 401 replay (#2904). Safe reads
     * (GET/HEAD) replay by default; anything that can change state does not,
     * because a backend 401 is not by itself proof that no side effect ran.
     * Set this only on call sites that have been audited — today that is the
     * Segment Analysis writes, which already refreshed-and-replayed through
     * their own wrapper before this flag existed.
     */
    authRetry?: boolean
    metadata?: {
      startTime?: number
    }
  }
  export interface AxiosResponse {
    correlationId?: string
  }
}

/** Per-request auth bookkeeping. Not part of the public config surface. */
type AuthAwareConfig = InternalAxiosRequestConfig & {
  __authSnapshot?: AuthSnapshot
  __authReplayed?: boolean
  __authManaged?: boolean
}

// Module-level coarse fallback: the correlation id of the most recent response
// (success or error) that carried an `x-correlation-id` header. Telemetry reads
// this when a specific response object isn't in scope at report time.
let lastCorrelationId: string | undefined

/**
 * The `x-correlation-id` of the most recently seen backend response, if any.
 * Best-effort breadcrumb for client error telemetry — not a strong guarantee
 * that the id belongs to the failing request.
 */
export function getLastCorrelationId(): string | undefined {
  return lastCorrelationId
}

const CORRELATION_HEADER = "x-correlation-id"

function readCorrelationId(headers: unknown): string | undefined {
  if (!headers || typeof headers !== "object") return undefined
  const bag = headers as Record<string, unknown> & {
    get?: (name: string) => string | null | undefined
  }
  // AxiosHeaders exposes a case-insensitive `get`; plain objects don't.
  const raw =
    typeof bag.get === "function"
      ? bag.get(CORRELATION_HEADER)
      : (bag[CORRELATION_HEADER] ?? bag[CORRELATION_HEADER.toUpperCase()])
  return typeof raw === "string" && raw ? raw : undefined
}

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  if (!config.headers["Content-Type"] && !(config.data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json"
  }

  const authAware = config as AuthAwareConfig
  authAware.__authSnapshot ??= captureAuthSnapshot()
  if (
    authAware.__authReplayed &&
    (!isAuthSnapshotCurrent(authAware.__authSnapshot) || config.signal?.aborted)
  ) {
    throw new Axios.CanceledError("Auth session changed before replay", undefined, config)
  }
  // Decide ownership before writing any header. A caller's Basic/custom bearer
  // remains theirs, even if they did not also set skipAuth.
  authAware.__authManaged ??=
    useLogin &&
    !config.skipAuth &&
    !(config.url && ABSOLUTE_URL.test(config.url)) &&
    !config.headers.has("Authorization")
  if (authAware.__authManaged) {
    try {
      SettingsService.getInstance()
    } catch (cause) {
      throw Object.assign(new Error("Cannot authorize request: settings are not initialized yet"), {
        config,
        cause,
      })
    }
    const adapter = Axios.getAdapter(config.adapter)
    config.adapter = (dispatchConfig) => {
      if (!isAuthSnapshotCurrent(authAware.__authSnapshot!) || dispatchConfig.signal?.aborted) {
        throw new Axios.CanceledError(
          "Auth session changed before dispatch",
          undefined,
          dispatchConfig
        )
      }
      // Bind the initial epoch to the credential sent after async interceptors,
      // while retaining the generation that originally owned this request.
      const dispatched = dispatchConfig as AuthAwareConfig
      if (!dispatched.__authReplayed) {
        dispatched.__authSnapshot = {
          ...captureAuthSnapshot(),
          generation: authAware.__authSnapshot!.generation,
        }
      }
      try {
        dispatchConfig.headers.set("Authorization", `Bearer ${SettingsService.getInstance().token}`)
      } catch (cause) {
        throw Object.assign(
          new Error("Cannot authorize request: settings are not initialized yet"),
          {
            config: dispatchConfig,
            cause,
          }
        )
      }
      return adapter(dispatchConfig)
    }
  }

  // Tender requests carry the per-browser admin-mode opt-in so the backend can
  // gate elevated tender-admin access on it. Harmless on non-tender routes; the
  // backend only honours it for real tender admins.
  if (config.url?.startsWith("/tender/")) {
    config.headers[TENDER_ADMIN_MODE_HEADER] = tenderAdminModeHeaderValue()
  }

  return config
}

const ABSOLUTE_URL = /^([a-z][a-z\d+\-.]*:)?\/\//i

/**
 * Whether a 401 on this request may be recovered by renewing the *app's* shared
 * session, and the request replayed once.
 *
 * Deliberately conservative. Safe, idempotent reads replay by default; a
 * mutation only replays when its call site opted in via `authRetry` after being
 * audited, because a 401 from the backend is not proof that the handler ran no
 * side effect. Requests that don't carry our credentials in the first place
 * (`skipAuth`, absolute/external URLs, or any request at all when login is
 * disabled) can never trigger a renewal of the shared session.
 */
function isReplayableUnauthorized(config: AuthAwareConfig | undefined): config is AuthAwareConfig {
  if (!config) return false
  if (!useLogin) return false
  if (config.skipAuth || config.authRetry === false || !config.__authManaged) return false
  // One replay per request, ever: a second 401 never starts another refresh.
  if (config.__authReplayed) return false
  if (config.signal?.aborted) return false
  if (config.url && ABSOLUTE_URL.test(config.url)) return false

  const method = (config.method ?? "get").toLowerCase()
  if (method === "get" || method === "head") return true
  return config.authRetry === true
}

export const api = Axios.create({
  baseURL: BASE_API_URL,
})

/**
 * Recovers a 401 through the shared refresh coordinator and replays the request
 * at most once. Returns `null` to let the original error reject untouched — a
 * failed recovery *or* a failed replay must surface the error the caller already
 * knows how to handle, never a new one. The success value is boxed so a replay
 * that legitimately resolves to `undefined` is still recognised as a success.
 */
async function replayUnauthorized(error: AxiosError): Promise<{ value: unknown } | null> {
  if (error.response?.status !== 401) return null

  const config = error.config as AuthAwareConfig | undefined
  if (!isReplayableUnauthorized(config)) return null

  const outcome = await recoverFromUnauthorized(config.__authSnapshot ?? captureAuthSnapshot(), {
    signal: config.signal as AbortSignal | undefined,
  })

  if (outcome.status !== "refreshed" && outcome.status !== "already-refreshed") return null

  if (
    !config.__authSnapshot ||
    !isAuthSnapshotCurrent(config.__authSnapshot) ||
    config.signal?.aborted
  )
    return null
  config.__authReplayed = true
  try {
    // Back through `api.request` on purpose: the request interceptor re-reads
    // the now-refreshed bearer, and the success interceptor still unwraps
    // `response.data` and records the correlation id, so the caller's contract
    // is identical to a first-attempt success.
    return { value: await api.request(config) }
  } catch {
    // Surface the original 401, not the replay's error.
    return null
  }
}

api.interceptors.request.use(authRequestInterceptor)

// Performance tracking interceptor for admin dashboard queries
api.interceptors.request.use((config) => {
  if (config.url?.startsWith("/admin/")) {
    config.metadata = { startTime: performance.now() }
  }
  return config
})

api.interceptors.response.use(
  (response) => {
    const correlationId = readCorrelationId(response.headers)
    if (correlationId) {
      lastCorrelationId = correlationId
      response.correlationId = correlationId
    }
    const config = response.config as InternalAxiosRequestConfig & {
      metadata?: { startTime?: number }
    }
    if (config.url?.startsWith("/admin/") && config.metadata?.startTime) {
      const duration = performance.now() - config.metadata.startTime
      const endpoint = config.url.replace("/api", "")
      // Log to console in development, could be sent to analytics in production
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(`[Admin Query] ${endpoint}: ${duration.toFixed(2)}ms`)
      }
    }
    return response.data
  },
  async (error: AxiosError) => {
    const correlationId = readCorrelationId(error.response?.headers)
    if (correlationId) {
      lastCorrelationId = correlationId
      // Attach onto the rejected error so callers (e.g. telemetry) can read the
      // id for the exact failing request rather than the coarse module fallback.
      ;(error as { correlationId?: string }).correlationId = correlationId
    }
    // `config` is absent when a request interceptor rejects — reading through it
    // unguarded threw a TypeError that replaced the original failure.
    const config = error.config as
      | (InternalAxiosRequestConfig & { metadata?: { startTime?: number } })
      | undefined
    if (config?.url?.startsWith("/admin/") && config.metadata?.startTime) {
      const duration = performance.now() - config.metadata.startTime
      const endpoint = config.url.replace("/api", "")
      // eslint-disable-next-line no-console
      console.error(`[Admin Query Failed] ${endpoint}: ${duration.toFixed(2)}ms`, error)
    }

    // Bounded 401 recovery (#2904). Anything that isn't a recoverable, replayable
    // 401 falls straight through to the original rejection below, so the error
    // contract every existing caller relies on is unchanged.
    const replayed = await replayUnauthorized(error)
    if (replayed) return replayed.value

    return Promise.reject(error)
  }
)
